import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import {
    expireMpin,
    getMpinSummary,
} from '../../store/actions/staffActionCreator';
import { showMessage } from '../../utils/helper';

/**
 * Admin view of one staff member's MPIN, with the vendor-side "Expire MPIN"
 * control. The button is enabled only while the rider has a PENDING reset
 * request (status ACTIVE + resetRequested) — the backend enforces the same
 * rule, this gating is UX, not security. Expiring drops the rider to the
 * fresh-setup flow at their next app login.
 */

interface MpinSummary {
    status?: 'ACTIVE' | 'EXPIRED' | 'SETUP_REQUIRED' | string;
    expiresAt?: string | number | null;
    resetRequested?: boolean;
    resetRequestedAt?: string | number | null;
}

export interface ResetMpinStaff {
    id: string | number;
    name?: string;
    contactNumber?: string;
}

const formatDate = (value?: string | number | null): string => {
    if (!value) return '-';
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
};

const STATUS_CHIP: Record<string, { label: string; bg: string; fg: string }> = {
    ACTIVE: { label: 'Active', bg: 'rgba(27, 181, 92, 0.12)', fg: '#1a7f43' },
    EXPIRED: { label: 'Expired', bg: 'rgba(245, 124, 0, 0.14)', fg: '#b45309' },
    SETUP_REQUIRED: {
        label: 'Not set up',
        bg: 'rgba(96, 108, 128, 0.14)',
        fg: '#4b5563',
    },
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(38, 50, 56, 0.06)',
};
const labelStyle: React.CSSProperties = {
    fontFamily: 'Nunito',
    fontSize: 13,
    color: '#5c6470',
};
const valueStyle: React.CSSProperties = {
    fontFamily: 'Nunito',
    fontSize: 14,
    fontWeight: 700,
    color: '#14112d',
};

const ResetMpinModal = ({
    open,
    staff,
    onClose,
}: {
    open: boolean;
    staff: ResetMpinStaff;
    onClose: () => void;
}) => {
    const dispatch = useDispatch();
    const [summary, setSummary] = useState<MpinSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [expiring, setExpiring] = useState(false);

    const fetchSummary = () => {
        setLoading(true);
        setLoadError(false);
        dispatch(
            getMpinSummary(
                staff.id,
                (envelope: any) => {
                    setSummary(envelope?.response ?? null);
                    setLoading(false);
                },
                () => {
                    setLoadError(true);
                    setLoading(false);
                },
            ),
        );
    };

    useEffect(() => {
        if (open && staff?.id) {
            setSummary(null);
            setConfirming(false);
            fetchSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, staff?.id]);

    const status = summary?.status ?? '';
    const chip = STATUS_CHIP[status] ?? {
        label: status || '—',
        bg: 'rgba(96, 108, 128, 0.14)',
        fg: '#4b5563',
    };
    const canExpire = status === 'ACTIVE' && summary?.resetRequested === true;
    const disabledReason =
        status === 'ACTIVE'
            ? "The rider hasn't requested a reset from the app. Ask them to tap 'Forgot PIN' in their app first."
            : "There's no active PIN to expire.";

    const handleExpire = () => {
        setExpiring(true);
        dispatch(
            expireMpin(
                staff.id,
                () => {
                    setExpiring(false);
                    setConfirming(false);
                    showMessage(
                        `PIN expired. ${staff.name ?? 'The rider'} will set a new PIN on next login.`,
                        'success',
                    );
                    // Refetch so the dialog reflects the new state (the
                    // pending request is consumed → button self-disables).
                    fetchSummary();
                },
                () => {
                    // Middleware already toasts the server message.
                    setExpiring(false);
                    setConfirming(false);
                },
            ),
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ style: { borderRadius: 12, minWidth: 440 } }}
        >
            <DialogTitle disableTypography>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography
                        style={{
                            fontFamily: 'Nunito',
                            fontWeight: 700,
                            fontSize: 18,
                        }}
                    >
                        PIN — {staff?.name ?? ''}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent style={{ paddingTop: 0 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" padding="32px">
                        <CircularProgress size={28} />
                    </Box>
                ) : loadError ? (
                    <Box padding="16px 0">
                        <Typography style={{ ...labelStyle, marginBottom: 12 }}>
                            Couldn't load the PIN summary.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            style={{ textTransform: 'none' }}
                            onClick={fetchSummary}
                        >
                            Retry
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box style={rowStyle}>
                            <span style={labelStyle}>Staff</span>
                            <span style={valueStyle}>
                                {staff?.name ?? '-'}
                                {staff?.contactNumber
                                    ? ` · ${staff.contactNumber}`
                                    : ''}
                            </span>
                        </Box>
                        <Box style={rowStyle}>
                            <span style={labelStyle}>PIN status</span>
                            <Chip
                                size="small"
                                label={chip.label}
                                style={{
                                    backgroundColor: chip.bg,
                                    color: chip.fg,
                                    fontFamily: 'Nunito',
                                    fontWeight: 700,
                                }}
                            />
                        </Box>
                        <Box style={rowStyle}>
                            <span style={labelStyle}>Expires on</span>
                            <span style={valueStyle}>
                                {formatDate(summary?.expiresAt)}
                            </span>
                        </Box>
                        <Box style={{ ...rowStyle, borderBottom: 'none' }}>
                            <span style={labelStyle}>Reset request</span>
                            <span style={valueStyle}>
                                {summary?.resetRequested
                                    ? `Requested ${moment(summary?.resetRequestedAt).isValid() ? moment(summary?.resetRequestedAt).fromNow() : ''}`
                                    : 'No reset request'}
                            </span>
                        </Box>

                        {confirming ? (
                            <Box
                                style={{
                                    backgroundColor: '#fdecea',
                                    borderRadius: 8,
                                    padding: '12px 14px',
                                    marginTop: 8,
                                }}
                            >
                                <Typography
                                    style={{
                                        fontFamily: 'Nunito',
                                        fontSize: 13.5,
                                        color: '#7a2d2d',
                                    }}
                                >
                                    This will invalidate the rider's PIN —
                                    they'll set a new one at next login. Expire
                                    now?
                                </Typography>
                            </Box>
                        ) : (
                            !canExpire && (
                                <Typography
                                    style={{
                                        ...labelStyle,
                                        marginTop: 8,
                                        fontSize: 12.5,
                                    }}
                                >
                                    {disabledReason}
                                </Typography>
                            )
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions style={{ padding: '12px 24px 20px' }}>
                <Button
                    onClick={confirming ? () => setConfirming(false) : onClose}
                    style={{ textTransform: 'none', fontFamily: 'Nunito' }}
                >
                    {confirming ? 'Back' : 'Close'}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!canExpire || loading || loadError || expiring}
                    onClick={
                        confirming ? handleExpire : () => setConfirming(true)
                    }
                    style={{
                        textTransform: 'none',
                        fontFamily: 'Nunito',
                        fontWeight: 700,
                        backgroundColor:
                            !canExpire || loading || loadError || expiring
                                ? undefined
                                : '#d32f2f',
                        color:
                            !canExpire || loading || loadError || expiring
                                ? undefined
                                : '#fff',
                    }}
                >
                    {expiring ? (
                        <CircularProgress size={20} style={{ color: '#fff' }} />
                    ) : confirming ? (
                        'Confirm expire'
                    ) : (
                        'Expire PIN'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResetMpinModal;
